import { DateTime, Duration } from 'luxon'

interface DateTimeSince {
  timeSince: string | null
  medDate: string | null
  time: string | null
}

const addOrdinals = (d: number): string => {
  if (d > 3 && d < 21) return `${d}th`
  switch (d % 10) {
    case 1:
      return `${d}st`
    case 2:
      return `${d}nd`
    case 3:
      return `${d}rd`
    default:
      return `${d}th`
  }
}

const ordinalRegex = /[A-Z][a-z]{2},\s(\d+)/g

const addOrdinalsToLuxon = (datestring: string): string => {
  if (datestring.length > 2) {
    const [day, num] = datestring.split(' ')
    return `${day} ${addOrdinals(parseInt(num))}`
  }
  return `${addOrdinals(parseInt(datestring))}`
}

export const useTimeSinceNow = (dateTimeString?: string): DateTimeSince => {
  if (!dateTimeString) {
    return { timeSince: null, medDate: null, time: null }
  }

  const dateTime = DateTime.fromISO(dateTimeString, { zone: 'utc' })
  const medDate = dateTime
    .toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY)
    .replace(ordinalRegex, addOrdinalsToLuxon)
  const time = dateTime.toLocaleString(DateTime.TIME_24_SIMPLE)

  const readable: string = toReadable('since', dateTime)

  return {
    timeSince: readable,
    medDate,
    time,
  }
}

export const eventDateAndTimeFormatting = (
  startDateTimeString: string,
  endDateTimeString: string
): string => {
  const start = DateTime.fromISO(startDateTimeString, { zone: 'utc' })
  const end = DateTime.fromISO(endDateTimeString, { zone: 'utc' })
  const diff = end.diff(start, ['years', 'minutes', 'hours', 'days', 'months'])
  const multipleDays = diff.as('hours') > 24

  const readableStart: string =
    `${start.toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY)}` +
    (!multipleDays
      ? `, ${start.toLocaleString(DateTime.TIME_24_SIMPLE)} - `
      : ' - ')
  const readableEnd: string = multipleDays
    ? end.toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY)
    : end.toLocaleString(DateTime.TIME_24_SIMPLE)
  const readableDuration: string = ` (${toReadable('duration', start, end)})`
  return (
    (readableStart + readableEnd).replace(ordinalRegex, addOrdinalsToLuxon) +
    readableDuration
  )
}

export const eventSubmissionFormatting = (createDate: string): string => {
  const { timeSince } = useTimeSinceNow(createDate)
  return timeSince as string
}
function toReadable(
  type: 'duration' | 'since',
  dateTime: DateTime,
  from: DateTime = DateTime.now()
): string {
  const difference = from.diff(dateTime)
  let readable: string
  if (difference.as('years') > 1) {
    readable = difference.toFormat("y 'years'", { floor: true })
  } else if (difference.as('months') > 1) {
    readable = difference.toFormat("M 'months'", { floor: true })
  } else if (difference.as('days') > 1) {
    const extra = Duration.fromObject({ days: type === 'duration' ? 1 : 0 })
    readable = difference.plus(extra).toFormat("d 'days'", { floor: true })
  } else if (difference.as('hours') > 1) {
    readable = difference.toFormat("h 'hours'", { floor: true })
  } else {
    readable = difference.toFormat("m 'minutes'", { floor: true })
  }
  return type === 'duration' ? readable : `${readable} ago`
}
